import React, { useContext, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  createBill,
  updateBill,
  getBillById,
  fetchCategories,
  getBillsSite,
} from "../../Services/billsService";
import { getUser } from "../../Services/userService";
import { Spinner } from "../../Components/Common";
import { UserContext } from "../../Context";
import FloatingInput from "../../Components/Common/FloatingInput";
import { HiOutlineArrowLeft } from "react-icons/hi";
import {
  FORM_MODES,
  VALIDATION_MESSAGES,
  FORM_TITLES,
  TOAST_MESSAGES,
  BILL_LABELS,
  MISC,
  BUTTON_TEXT,
} from "./constants";
import { updateBillStatus, deleteBill } from "../../Services/billsService";
import { FiTrash2 } from "react-icons/fi";
import { ROLES } from "../../Constants/Strings";
import { HiViewfinderCircle } from "react-icons/hi2";
import { BiHide } from "react-icons/bi";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { toProperCase } from "../../Utils";

const validationSchema = Yup.object().shape({
  date: Yup.date().required(VALIDATION_MESSAGES.DATE_REQUIRED),
  amount: Yup.number()
    .positive("Amount must be positive")
    .required(VALIDATION_MESSAGES.AMOUNT_REQUIRED),
  category: Yup.string().required(VALIDATION_MESSAGES.CATEGORY_REQUIRED),
  bill_file: Yup.mixed().required(VALIDATION_MESSAGES.FILE_REQUIRED),
});

const BillOperations = () => {
  const { billType, billId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, nonStaffAccess } = useContext(UserContext);

  const [mode, setMode] = useState(FORM_MODES.ADD);
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showBill, setShowBill] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [sites, setSites] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [showBillPhoto, setShowBillPhoto] = useState(false);

  const isViewMode = mode === FORM_MODES.VIEW;
  const isPending =
    initialValues?.status === "PENDING_L1_APPROVAL" ||
    initialValues?.status === "PENDING_L2_APPROVAL";

  useEffect(() => {
    if (location.pathname.includes("view")) setMode(FORM_MODES.VIEW);
    else setMode(FORM_MODES.ADD);
  }, [location.pathname]);

  const fetchCategoriesData = async (type) => {
    try {
      const res = await fetchCategories({ type: type?.toUpperCase() });
      setCategories(res?.data?.results || []);
    } catch {
      toast.error(TOAST_MESSAGES.BILLS_FETCH_ERROR);
      setCategories([]);
    }
  };

  useEffect(() => {
    if (mode === FORM_MODES.VIEW && billId) {
      setLoading(true);
      getBillById(billId)
        .then((res) => setInitialValues(res.data))
        .catch(() => toast.error(TOAST_MESSAGES.BILL_UPDATE_ERROR))
        .finally(() => setLoading(false));
    } else {
      const today = new Date().toISOString().split("T")[0];
      setInitialValues({
        site: "",
        date: today,
        amount: "",
        status: "",
        category: "",
        type: billType || "",
        user: "",
        bill_file: null,
      });
    }
  }, [mode, billId, billType]);

  useEffect(() => {
    if (mode === FORM_MODES.ADD) {
      getBillsSite()
        .then((res) => {
          setSites(res?.data || []); // API returns array of sites directly
        })
        .catch(() => {
          toast.error("Failed to fetch sites");
          setSites([]);
        });
    }
  }, [mode]);

  useEffect(() => {
    const fetchLoggedInUser = async () => {
      try {
        const res = await getUser();
        setLoggedInUser(res?.data);
      } catch (err) {
        toast.error(TOAST_MESSAGES.USER_FETCH_ERROR);
      }
    };
    fetchLoggedInUser();
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      setSubmitting(true);
      setSubmitting(true);
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (values[key]) formData.append(key, values[key]);
      });

      if (mode === FORM_MODES.ADD) {
        const response = await createBill(formData);
        if(response.status === 201){
          toast.success(TOAST_MESSAGES.BILL_ADD_SUCCESS);
        } else{
          const msg = extractErrorMessage(response) || TOAST_MESSAGES.BILL_ADD_ERROR;
          toast.error(msg);
        }
      } else {
        const response = await updateBill(initialValues.id, formData);
        if(response.status === 200){
          toast.success(TOAST_MESSAGES.BILL_UPDATE_SUCCESS);
        } else {
          const msg = extractErrorMessage(response) || TOAST_MESSAGES.BILL_UPDATE_SUCCESS;
        toast.error(msg);
        }
      }
      navigate(-1);
    } catch (err) {
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.BILL_ADD_ERROR;
      toast.error(msg);
    } finally {
      setSubmitting(false);
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      const response = await updateBillStatus(initialValues.id, {
        action: status,
      });
      if (response?.status === 200) {
        toast.success(TOAST_MESSAGES.STATUS_UPDATE_SUCCESS(status));
        navigate(-1);
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.BILL_STATUS_ERROR;
        toast.error(msg);
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.BILL_STATUS_ERROR;
      toast.error(msg);
    }
  };

  const handleBillDelete = async (id) => {
    try {
      await deleteBill(id);
      toast.success(TOAST_MESSAGES.BILL_DELETE);
      navigate(-1);
    } catch {
      toast.error(TOAST_MESSAGES.BILL_DELETE_ERROR);
    }
  };

  const commonInput =
    "border rounded-md px-3 py-2 w-full focus:ring-2 focus:ring-blue-400 focus:outline-none";

  if (loading || !initialValues) {
    return (
      <div className="flex justify-center items-center h-40">
        <Spinner size={6} />
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-primary text-2xl disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={submitting}
        >
          <HiOutlineArrowLeft />
        </button>
        <h2 className="text-lg md:text-xl text-primary font-semibold text-center flex-1">
          {isViewMode ? FORM_TITLES.VIEW : FORM_TITLES.ADD}
        </h2>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={isViewMode ? null : validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, isSubmitting, setFieldValue }) => (
          <Form className="flex flex-col">
            {/* ADD MODE */}
            {!isViewMode && (
              <>
                {/* Site Dropdown */}
                <div className="mb-4">
                  <label className="text-sm font-semibold mb-1">
                    {BILL_LABELS.site}
                  </label>
                  <Field
                    as="select"
                    name="site"
                    className={commonInput}
                    disabled={isSubmitting || sites.length === 0}
                  >
                    <option value="">Select Site</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.site_name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="site"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                <div className="flex gap-2">
                  {/* Date */}
                  <div className="mb-4 w-1/2">
                    <label className="text-sm font-semibold mb-1">
                      {BILL_LABELS.date}
                    </label>
                    <Field
                      type="date"
                      name="date"
                      className={commonInput}
                      disabled={isSubmitting || submitting}
                    />
                    <ErrorMessage
                      name="date"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                  {/* Amount */}
                  <div className="mb-4">
                    <label className="text-sm font-semibold mb-1">
                      {BILL_LABELS.amount}
                    </label>
                    <Field
                      type="number"
                      name="amount"
                      className={commonInput}
                      disabled={isSubmitting || submitting}
                    />
                    <ErrorMessage
                      name="amount"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>

                <div className="flex gap-2 ">
                  {/* Type */}
                  <div className="mb-4 w-1/2">
                    <label className="text-sm font-semibold mb-1">
                      {BILL_LABELS.type}
                    </label>
                    <Field
                      as="select"
                      name="type"
                      className={commonInput}
                      disabled={isSubmitting || submitting}
                      onChange={(e) => {
                        const selectedType = e.target.value;
                        setFieldValue("type", selectedType);
                        setFieldValue("category", "");
                        fetchCategoriesData(selectedType);
                      }}
                    >
                      <option value="">{`Select ${BILL_LABELS.type}`}</option>
                      <option value="PURCHASE">PURCHASE</option>
                      <option value="REIMBURSEMENT">REIMBURSEMENT</option>
                    </Field>
                    <ErrorMessage
                      name="type"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  {/* Category */}
                  <div className="mb-4 w-1/2">
                    <label className="text-sm font-semibold mb-1">
                      {BILL_LABELS.category}
                    </label>
                    <Field
                      as="select"
                      name="category"
                      className={commonInput}
                      disabled={isSubmitting || !values.type}
                    >
                      <option value="">{`Select ${BILL_LABELS.category}`}</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="category"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>

                {/* File Upload */}
                <div className="mb-4">
                  <label className="text-sm font-semibold text-primary mb-1">
                    Bill File
                  </label>
                  <input
                    type="file"
                    name="bill_file"
                    accept="image/*,application/pdf"
                    className={commonInput}
                    onChange={(event) => {
                      const file = event.currentTarget.files[0];
                      setFieldValue("bill_file", file);
                      if (file) {
                        setPreviewUrl(URL.createObjectURL(file));
                        setShowBill(true);
                      }
                    }}
                    disabled={isSubmitting || submitting}
                  />
                  <ErrorMessage
                    name="bill_file"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />

                  {/* Preview after selecting a file */}
                  {showBill && previewUrl && (
                    <div className="mt-2 flex justify-center">
                      {/\.(pdf)$/i.test(selectedFile?.name || "") ? (
                        <embed
                          src={previewUrl}
                          type="application/pdf"
                          className="w-full h-64 border rounded"
                        />
                      ) : (
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="max-w-full md:max-w-md rounded-lg shadow-md"
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                {!isViewMode && (
                  <div className="flex justify-between gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded font-semibold text-base disabled:cursor-not-allowed"
                      disabled={isSubmitting || submitting}
                    >
                      {BUTTON_TEXT.CANCEL}
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || submitting}
                      className="bg-primary hover:bg-secondary text-white px-5 py-2 rounded-md"
                    >
                      {isSubmitting
                        ? BUTTON_TEXT.SUBMITTING
                        : BUTTON_TEXT.SUBMIT}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* VIEW MODE */}
            {isViewMode && (
              <>
                <div className="flex gap-2">
                  <div className="w-1/2">
                    <FloatingInput
                      label={BILL_LABELS.project_name}
                      name="project_name"
                      value={initialValues.project_name || MISC.NOT_AVAILABLE}
                      disabled
                    />
                  </div>
                  <div className="w-1/2">
                    <FloatingInput
                      label={BILL_LABELS.site}
                      name="site_name"
                      value={initialValues.site_name || MISC.NOT_AVAILABLE}
                      disabled
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="w-1/2">
                    <FloatingInput
                      label={BILL_LABELS.date}
                      name="date"
                      value={initialValues.date || MISC.NOT_AVAILABLE}
                      disabled
                    />
                  </div>

                  <div className="w-1/2">
                    <FloatingInput
                      label={BILL_LABELS.user_name}
                      name="user_name"
                      value={initialValues.user_name || MISC.NOT_AVAILABLE}
                      disabled
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="w-1/2">
                    <FloatingInput
                      label={BILL_LABELS.amount}
                      name="amount"
                      value={initialValues.amount || MISC.NOT_AVAILABLE}
                      disabled
                    />
                  </div>
                  <div className="w-1/2">
                    <FloatingInput
                      label={BILL_LABELS.status}
                      name="status"
                      value={
                        toProperCase(initialValues.status) || MISC.NOT_AVAILABLE
                      }
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="w-1/2">
                    <FloatingInput
                      label={BILL_LABELS.type}
                      name="type"
                      value={initialValues.type || MISC.NOT_AVAILABLE}
                      disabled
                    />
                  </div>
                  <div className="w-1/2">
                    <FloatingInput
                      label={BILL_LABELS.category}
                      name="category_name"
                      value={initialValues.category_name || MISC.NOT_AVAILABLE}
                      disabled
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold mb-1">
                    Bill File
                  </label>
                  {initialValues.bill_file ? (
                    <>
                      <button
                        type="button"
                        className="text-primary underline flex items-center gap-1 mb-1"
                        onClick={() => setShowBillPhoto(!showBillPhoto)}
                      >
                        {showBillPhoto ? (
                          <div className="flex items-center gap-1">
                            <BiHide />
                            <span>Hide File</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <HiViewfinderCircle />
                            <span>View File</span>
                          </div>
                        )}
                      </button>

                      {showBillPhoto && (
                        <div className="mt-2 flex justify-center">
                          {/\.(pdf)$/i.test(initialValues.bill_file) ? (
                            <embed
                              src={initialValues.bill_file}
                              type="application/pdf"
                              className="w-full h-64 border rounded"
                            />
                          ) : (
                            <img
                              src={initialValues.bill_file}
                              alt="Bill"
                              className="w-full max-h-96 rounded border object-contain"
                            />
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500 text-sm">
                      {MISC.NO_BILLS_FOUND}
                    </p>
                  )}
                </div>

                <div
                  className={`flex ${
                    nonStaffAccess && isPending
                      ? "justify-center gap-4"
                      : "justify-center"
                  } mt-6`}
                >
                  {isPending && (
                    <>
                      {/* L1 Approval - Project Engineer */}
                      {user?.role === ROLES.PROJECT_ENGINEER &&
                        initialValues?.status === "PENDING_L1_APPROVAL" && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleStatusChange("approve")}
                              className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={submitting}
                            >
                              {BUTTON_TEXT.APPROVE}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleStatusChange("reject")}
                              className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={submitting}
                            >
                              {BUTTON_TEXT.REJECT}
                            </button>
                          </>
                        )}

                      {/* L2 Approval - Project Manager */}
                      {user?.role === ROLES.PROJECT_MANAGER &&
                        initialValues?.status === "PENDING_L2_APPROVAL" && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleStatusChange("approve")}
                              className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md"
                            >
                              {BUTTON_TEXT.APPROVE}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleStatusChange("reject")}
                              className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-md"
                            >
                              {BUTTON_TEXT.REJECT}
                            </button>
                          </>
                        )}
                    </>
                  )}

                  <button
                    type="button"
                    className={`pr-5 py-2 ${
                      loggedInUser?.id === initialValues?.user && isPending
                        ? "text-red-600 cursor-pointer"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                    onClick={() => {
                      if (
                        !(loggedInUser?.id === initialValues?.user && isPending)
                      )
                        return;
                      const confirmed = window.confirm(
                        TOAST_MESSAGES.DELETE_ALERT
                      );
                      if (confirmed) {
                        handleBillDelete(initialValues.id);
                      }
                    }}
                    title={BUTTON_TEXT.DELETE_BILL}
                    disabled={
                      !(loggedInUser?.id === initialValues?.user && isPending)
                    }
                  >
                    <FiTrash2 className="w-7 h-7" />
                  </button>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default BillOperations;
